import { useApolloClient } from "@apollo/client";
import { useEffect, useRef } from "react";

const REFETCH_ON_FOCUS_AFTER_MS = 30 * 1000;
const REFETCH_WHILE_VISIBLE_AFTER_MS = 5 * 60 * 1000;

const isOlderThan = ({ lastRefetchedAt, now, ageMs }) =>
  now - lastRefetchedAt >= ageMs;

/**
 * Refetches the named active queries when the user comes back to the tab, or
 * periodically while it stays open, so long-lived pages do not edit stale rows.
 * Only queries that are mounted and not skipped are refetched.
 */
const useRefetchWhenVisible = ({
  queryNames = [],
  refetchOnFocusAfterMs = REFETCH_ON_FOCUS_AFTER_MS,
  refetchWhileVisibleAfterMs = REFETCH_WHILE_VISIBLE_AFTER_MS,
  documentObject = typeof document === "undefined" ? null : document,
  windowObject = typeof window === "undefined" ? null : window,
} = {}) => {
  const client = useApolloClient();
  const lastRefetchedAtRef = useRef(Date.now());
  const queryNamesKey = queryNames.join(",");

  useEffect(() => {
    if (!documentObject || !windowObject || !queryNamesKey) return undefined;

    const refetchIfOlderThan = ageMs => {
      const now = Date.now();
      if (
        documentObject.visibilityState !== "visible" ||
        !isOlderThan({
          lastRefetchedAt: lastRefetchedAtRef.current,
          now,
          ageMs,
        })
      ) {
        return;
      }

      lastRefetchedAtRef.current = now;
      Promise.resolve(
        client.refetchQueries({ include: queryNamesKey.split(",") }),
      ).catch(() => null);
    };

    const refetchOnReturn = () => refetchIfOlderThan(refetchOnFocusAfterMs);
    const intervalId = windowObject.setInterval(
      () => refetchIfOlderThan(refetchWhileVisibleAfterMs),
      refetchOnFocusAfterMs,
    );
    documentObject.addEventListener("visibilitychange", refetchOnReturn);
    windowObject.addEventListener("focus", refetchOnReturn);

    return () => {
      windowObject.clearInterval(intervalId);
      documentObject.removeEventListener("visibilitychange", refetchOnReturn);
      windowObject.removeEventListener("focus", refetchOnReturn);
    };
  }, [
    client,
    documentObject,
    queryNamesKey,
    refetchOnFocusAfterMs,
    refetchWhileVisibleAfterMs,
    windowObject,
  ]);
};

export { isOlderThan };
export default useRefetchWhenVisible;
